import React, { useState, useMemo, useEffect } from 'react';
import {
    DndContext,
    DragEndEvent,
    DragOverlay,
    PointerSensor,
    useSensor,
    useSensors,
    closestCenter,
    DragStartEvent
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import ModuleSetTree from './ModuleSetTree';
import BuilderPanel from './BuilderPanel';
import { PathwayItem, Module, Set } from '../types';
import { v4 as uuidv4 } from 'uuid';
import "../../LearningPathway.css";
import { clearPathwayStore, setStep } from '@app/store/reducers/learningPathway';
import { useDispatch, useSelector } from 'react-redux';
import { createLearningPathway, updatePathwayConfiguration } from '@app/services/learningPathway';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@components';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css'; 
import { useDecodedParam } from '@app/hooks/useDecodedParam';
import { decodeId } from '@app/helpers/base64url';

const DualPaneBuilder: React.FC = () => {
    const [selectedSubject, setSelectedSubject] = useState<string>('1');
    const [pathwayItems, setPathwayItems] = useState<PathwayItem[]>([]);
    const [activeId, setActiveId] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [selectedSets, setSelectedSets] = useState<{ moduleId: string; setId: string }[]>([]);
    const [isLoading, setLoading] = useState(false);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { type, id: encodedId } = useParams();
    const id = encodedId ? decodeId(encodedId) : null;
    const lpStore = useSelector((state: any) => state.learningPathway);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        })
    );

    useEffect(() => {
        if(id){
           setPathwayItems(lpStore?.pathway)
        }
    },[]);

    const allModules = lpStore?.modules ? lpStore?.modules : [];

    // Filter modules based on search and what's already in pathway
    const availableModules = useMemo(() => {
        return allModules.filter(module => {
            // Check if module name matches search
            const matchesSearch = module.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                module.description.toLowerCase().includes(searchQuery.toLowerCase());

            if (!matchesSearch) return false;

            // Check if entire module is in pathway
            const moduleInPathway = pathwayItems.some(
                item => item.module_id === module.id && item.type === 'module'
            );

            if (moduleInPathway) return false;

            // Check if all sets are individually added
            const allSetsAdded = module.sets.every(set =>
                pathwayItems.some(item =>
                    item.module_id === module.id &&
                    item.set_id === set.id &&
                    item.type === 'set'
                )
            );

            return !allSetsAdded;
        }).map(module => {
            // For each module, filter out sets that are already in pathway
            const availableSets = module.sets.filter(set =>
                !pathwayItems.some(item =>
                    item.module_id === module.id &&
                    item.set_id === set.id
                )
            );

            return { ...module, sets: availableSets };
        });
    }, [allModules, pathwayItems, searchQuery]);

    const handleAddModule = (module: Module) => {
        // Remove all individual sets from pathway if adding entire module
        const withoutSets = pathwayItems.filter(item =>
            !(item.module_id === module.id && item.type === 'set')
        );

        const newItem: PathwayItem = {
            id: uuidv4(),
            type: 'module',
            name: module.name,
            description: module.description,
            module_id: module.id,
            order: withoutSets.length + 1,
            worksheet_from: module.worksheet_from,
            worksheet_to: module.worksheet_to
        };

        setPathwayItems([...withoutSets, newItem]);
    };

    const handleAddSet = (module: Module, set: Set) => {
        const newItem: PathwayItem = {
            id: uuidv4(),
            type: 'set',
            name: set.name,
            description: set.description,
            module_id: module.id,
            set_id: set.id,
            order: pathwayItems.length + 1,
            worksheet_from: set.worksheet_from,
            worksheet_to: set.worksheet_to
        };

        setPathwayItems([...pathwayItems, newItem]);
    };

    const handleToggleSetSelection = (moduleId: string, setId: string) => {
        setSelectedSets(prev => {
            const exists = prev.some(s => s.moduleId === moduleId && s.setId === setId);
            if (exists) {
                return prev.filter(s => !(s.moduleId === moduleId && s.setId === setId));
            }
            return [...prev, { moduleId, setId }];
        });
    };

    const handleToggleModuleSelection = (module: Module, checked: boolean) => {
        setSelectedSets(prev => {
            // Remove all sets of this module first
            const withoutModule = prev.filter(s => s.moduleId !== module.id);
            if (checked) {
                // Add all available sets of this module
                const moduleSets = module.sets.map(set => ({ moduleId: module.id, setId: set.id }));
                return [...withoutModule, ...moduleSets];
            }
            return withoutModule;
        });
    };

    const handleAddSelectedSets = () => {
        if (selectedSets.length === 0) return;

        let updatedItems = [...pathwayItems];
        let orderCounter = pathwayItems.length;

        // Iterate through available modules to maintain set order
        availableModules.forEach((module: Module) => {
            // Get the original (unfiltered) module to know its full set count
            const originalModule = allModules.find((m: Module) => m.id === module.id);
            const originalSetCount = originalModule ? originalModule.sets.length : module.sets.length;

            // Count how many sets of this module are currently selected
            const selectedCountForModule = module.sets.filter(set =>
                selectedSets.some(s => s.moduleId === module.id && s.setId === set.id)
            ).length;

            // Treat as a whole module ONLY if every original set of the module is being added
            // (i.e. no sets of this module are already in the pathway and all are selected)
            const isWholeModule = originalSetCount > 0 && selectedCountForModule === originalSetCount;

            if (isWholeModule) {
                // Add as a whole module - remove any individual sets of this module first
                updatedItems = updatedItems.filter(item =>
                    !(item.module_id === module.id && item.type === 'set')
                );
                orderCounter = updatedItems.length + 1;
                updatedItems.push({
                    id: uuidv4(),
                    type: 'module',
                    name: module.name,
                    description: module.description,
                    module_id: module.id,
                    order: orderCounter,
                    worksheet_from: module.worksheet_from,
                    worksheet_to: module.worksheet_to
                });
            } else {
                // Add only the selected individual sets
                module.sets.forEach((set: Set) => {
                    const isSelected = selectedSets.some(
                        s => s.moduleId === module.id && s.setId === set.id
                    );
                    if (isSelected) {
                        orderCounter += 1;
                        updatedItems.push({
                            id: uuidv4(),
                            type: 'set',
                            name: set.name,
                            description: set.description,
                            module_id: module.id,
                            set_id: set.id,
                            order: orderCounter,
                            worksheet_from: set.worksheet_from,
                            worksheet_to: set.worksheet_to
                        });
                    }
                });
            }
        });

        // Re-index order to keep it sequential
        updatedItems = updatedItems.map((item, index) => ({ ...item, order: index + 1 }));

        setPathwayItems(updatedItems);
        setSelectedSets([]);
    };

    const handleRemove = (id: string | string[]) => {
        // Handle both single ID and array of IDs (for consolidated sets)
        const idsToRemove = Array.isArray(id) ? id : [id];
        
        const updated = pathwayItems
            .filter(item => !idsToRemove.includes(item.id))
            .map((item, index) => ({ ...item, order: index + 1 }));
        setPathwayItems(updated);
    };

    const handleDragStart = (event: DragStartEvent) => {
        setActiveId(event.active.id as string);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        setActiveId(null);
        const { active, over } = event;

        // Check if dragging from left panel (module or set)
        if (active.data.current?.type === 'module') {
            handleAddModule(active.data.current.module);
            return;
        }

        if (active.data.current?.type === 'set') {
            handleAddSet(active.data.current.module, active.data.current.set);
            return;
        }

        // Reordering within builder panel
        if (over && active.id !== over.id) {
            const oldIndex = pathwayItems.findIndex(item => item.id === active.id);
            const newIndex = pathwayItems.findIndex(item => item.id === over.id);

            if (oldIndex !== -1 && newIndex !== -1) {
                const reordered = arrayMove(pathwayItems, oldIndex, newIndex);
                const updated = reordered.map((item, index) => ({ ...item, order: index + 1 }));
                setPathwayItems(updated);
            }
        }
    };

    const handleBack = () => {
        dispatch(setStep(1))
    }

    const handleSubmit = async() => {
        try {
            setLoading(true);
            let payload = {
                step: 2,
                name: lpStore?.name,
                franchise_id: lpStore?.franchise_id ? lpStore?.franchise_id : null,
                subject_id: lpStore?.subject_id,
                status: lpStore?.status,
                pathway: pathwayItems
            }
            let resp;
            if(type === 'edit'){
                if(lpStore?.student_assigned > 0){
                    confirmAlert({
                        customUI: ({ onClose }) => (
                            <div className="lp-confirm">
                                <h2 className="lp-confirm__title">Confirm to submit</h2>
                                <p className="lp-confirm__text">
                                    Are you sure you want to update this pathway?
                                    <br />Currently, &nbsp;
                                    <span className="lp-confirm__count">
                                        <b>{lpStore?.student_assigned ?? 0} students</b>
                                    </span>
                                    &nbsp; are assigned to it.
                                </p>

                                <div className="lp-confirm__actions">
                                    <button
                                        className="lp-confirm__btn lp-confirm__btn--secondary"
                                        onClick={() => {
                                            setLoading(false);
                                            onClose();
                                        }}
                                    >
                                        No
                                    </button>
                                    <button
                                        className="lp-confirm__btn lp-confirm__btn--primary"
                                        onClick={() => {
                                            updatePathwayConfigurationAPI();
                                            onClose();
                                        }}
                                    >
                                        Yes
                                    </button>
                                </div>
                            </div>
                        ),
                    });
                }else{
                    updatePathwayConfigurationAPI()
                }
            }else{
                resp = await createLearningPathway(payload);
                if(resp.success === true){
                    setLoading(false);
                    toast.success(resp?.message);
                    dispatch(clearPathwayStore());
                    navigate('/learning-pathway');
                }else{
                    setLoading(false);
                    toast.error(resp?.errors[0]?.msg);
                }
            }
        } catch (error: any) {
            setLoading(false);
            toast.error(error?.message || 'Internal server error')
        }
    }

    const updatePathwayConfigurationAPI = async() => {
        try {
            setLoading(true);
            const resp = await updatePathwayConfiguration( {
                learning_pathway_id: id,
                step: 2,
                name: lpStore?.name,
                franchise_id: lpStore?.franchise_id ? lpStore?.franchise_id : null,
                subject_id: lpStore?.subject_id,
                status: lpStore?.status,
                pathway: pathwayItems
            });
            if(resp.success === true){
                setLoading(false);
                toast.success(resp?.message);
                dispatch(clearPathwayStore());
                navigate('/learning-pathway');
            }else{
                setLoading(false);
                toast.error(resp?.errors[0]?.msg);
            }
        } catch (error: any) {
            setLoading(false);
            toast.error(error?.message || 'Internal server error')
        }
    }

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-md-12">
                    <div className="card card-primary" style={{ backgroundColor: '#f5f5f5' }}>
                        <div className="card-body">
                            <div className="card card-primary">
                             <div className="card-body">
                                <div className='row'>
                                    <div className='col-sm-3'>
                                        <dt>Pathway Name</dt>
                                        <dd>{ lpStore?.name ?? '---' }</dd>
                                    </div>
                                    <div className='col-sm-3'>
                                        <dt>Franchise</dt>
                                        <dd>{ lpStore?.franchise_name == '' ? '---' : lpStore?.franchise_name }</dd>
                                    </div>
                                    <div className='col-sm-3'>
                                        <dt>Subject</dt>
                                        <dd>{ lpStore?.subject_name == '' ? '---' : lpStore?.subject_name }</dd>
                                    </div>
                                    <div className='col-sm-3'>
                                        <dt>Status</dt>
                                        <dd>{ lpStore?.status ? lpStore?.status.charAt(0).toUpperCase() + lpStore?.status.slice(1) : '---' }</dd>
                                    </div>
                                </div>
                            </div>
                             </div>
                            <div className="dual-pane-container">
                                <DndContext
                                    sensors={sensors}
                                    onDragStart={handleDragStart}
                                    onDragEnd={handleDragEnd}
                                    collisionDetection={closestCenter}
                                >
                                    <div className="panes-wrapper">
                                        <div className="left-pane">
                                            <div className="pane-header">
                                                <h2>
                                                    📚 Available Modules & Sets
                                                    <span 
                                                        title="Drag modules or individual sets to the right panel, or use multiselect and add to build your pathway" 
                                                        style={{
                                                            marginLeft: '8px',
                                                            cursor: 'help',
                                                            fontSize: '16px',
                                                            color: '#667eea'
                                                        }}
                                                    >
                                                        <i className="fas fa-info-circle"></i>
                                                    </span>
                                                </h2>
                                                <p>Drag or multiselect items to add to your pathway</p>

                                                {selectedSubject && (
                                                    <div style={{ marginTop: '15px' }}>
                                                        <input
                                                            type="text"
                                                            placeholder="Search modules..."
                                                            value={searchQuery}
                                                            onChange={(e) => setSearchQuery(e.target.value)}
                                                            style={{
                                                                width: '100%',
                                                                padding: '10px 15px',
                                                                border: '1px solid #e1e5e9',
                                                                borderRadius: '6px',
                                                                fontSize: '14px'
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                                {selectedSets.length > 0 && (
                                                    <div style={{
                                                        marginTop: '12px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        padding: '8px 12px',
                                                        backgroundColor: '#eef2ff',
                                                        borderRadius: '6px',
                                                        border: '1px solid #c7d2fe'
                                                    }}>
                                                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#4338ca' }}>
                                                            {selectedSets.length} set(s) selected
                                                        </span>
                                                        <div style={{ display: 'flex', gap: '8px' }}>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-secondary"
                                                                onClick={() => setSelectedSets([])}
                                                            >
                                                                Clear
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btnLP"
                                                                onClick={handleAddSelectedSets}
                                                            >
                                                                <i className="fas fa-plus" style={{ marginRight: '4px' }}></i>
                                                                Add Selected
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            <ModuleSetTree
                                                modules={availableModules}
                                                onAddModule={handleAddModule}
                                                onAddSet={handleAddSet}
                                                selectedSets={selectedSets}
                                                onToggleSetSelection={handleToggleSetSelection}
                                                onToggleModuleSelection={handleToggleModuleSelection}
                                            />
                                        </div>

                                        <div className="right-pane">
                                            <div className="pane-header">
                                                <h2>
                                                    🎯 Your Learning Pathway
                                                    <span 
                                                        title="Consecutive sets from the same module will be shown as consolidated ranges for better readability" 
                                                        style={{
                                                            marginLeft: '8px',
                                                            cursor: 'help',
                                                            fontSize: '16px',
                                                            color: '#667eea'
                                                        }}
                                                    >
                                                        <i className="fas fa-info-circle"></i>
                                                    </span>
                                                </h2>
                                                <p>
                                                    {pathwayItems.length === 0
                                                        ? 'Start building by dragging items from the left'
                                                        : `${pathwayItems.length} item(s) in your pathway`}
                                                </p>
                                            </div>
                                            <BuilderPanel items={pathwayItems} onRemove={handleRemove} />
                                        </div>
                                    </div>
                                    <DragOverlay>
                                        {activeId ? (
                                            <div style={{
                                                padding: '15px',
                                                background: 'white',
                                                border: '2px solid #667eea',
                                                borderRadius: '6px',
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                                cursor: 'grabbing'
                                            }}>
                                                Dragging...
                                            </div>
                                        ) : null}
                                    </DragOverlay>
                                </DndContext>
                            </div>
                        </div>
                        <div className="card-footer">
                            <Button onClick={handleBack} className="btn btn-default mr-2">Back </Button>
                            { type === 'edit' ? (
                                <Button type="submit" isLoading={isLoading} onClick={handleSubmit} className="btn btnLP" disabled={pathwayItems.length == 0}>Update Learning Pathway</Button>
                            ):(
                                <Button type="submit" isLoading={isLoading} onClick={handleSubmit} className="btn btnLP" disabled={pathwayItems.length == 0}>Create Learning Pathway</Button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DualPaneBuilder;